import axios, { HttpStatusCode } from "axios";
import { createApi } from "@reduxjs/toolkit/query/react";
import { authEndpoints } from "./endpoints/authEndpoints";
import { dataEndpoints } from "./endpoints/dataEndpoints";
import { toastUtils } from "../Utils/helper";

const axiosInstance = axios.create();

const getToken = (key) =>
  localStorage.getItem(key) || sessionStorage.getItem(key);

// const setToken = (key, value) => {
//   if (localStorage.getItem(key)) {
//     localStorage.setItem(key, value);
//   } else if (sessionStorage.getItem(key)) {
//     sessionStorage.setItem(key, value);
//   }
// };

const removeToken = (key) => {
  localStorage.removeItem(key);
  sessionStorage.removeItem(key);
};

const axiosBaseQuery =
  ({ baseUrl }) =>
  async ({ url, method, data, params, timeout, headers = {} }) => {
    let accessToken = getToken("accessToken");
    const refreshToken = getToken("refreshToken");

    try {
      const result = await axiosInstance({
        url: baseUrl + url,
        method,
        data,
        params,
        timeout: timeout || 60000,
        headers: {
          Authorization: `Bearer ${accessToken}`,
          ...headers,
        },
      });
      return { data: result.data };
    } catch (axiosError) {
      const err = axiosError;

      if (err.code === "ECONNABORTED") {
        toastUtils.error("Please try again");
      }

      if (
        [HttpStatusCode.NotFound, HttpStatusCode.InternalServerError].includes(
          err.response?.status,
        )
      ) {
        toastUtils.error("Something went wrong. Please try again.");
      }

      if (err.response?.status === 401) {
        // Nếu có refreshToken và token hết hạn
        if (refreshToken) {
          try {
            const refreshResponse = await axiosInstance.post(
              `${baseUrl}/api/v1/consumers/auth/refresh`,
              undefined,
              {
                headers: {
                  Authorization: `Bearer ${refreshToken}`,
                  accept: "application/json",
                },
              },
            );
            console.log("Refresh response:", refreshResponse.data);
            let accessTokenNew = refreshResponse.data.data.access_token;

            localStorage.setItem(
              "accessToken",
              refreshResponse.data.data.access_token,
            );
            localStorage.setItem(
              "refreshToken",
              refreshResponse.data.data.refresh_token,
            );
            // Retry request với token mới
            const retryResult = await axiosInstance({
              url: baseUrl + url,
              method,
              data,
              params,
              timeout: timeout || 60000,
              headers: {
                Authorization: `Bearer ${accessTokenNew}`,
                ...headers,
              },
            });
            return { data: retryResult.data };
          } catch (refreshError) {
            // Refresh cũng fail
            if (refreshError.code === "ECONNABORTED") {
              toastUtils.error("Please try again");
              return {
                error: {
                  status: 408,
                  data: "Request Timeout",
                },
              };
            }

            console.error("Refresh token failed:", refreshError);
            removeToken("accessToken");
            removeToken("refreshToken");
            window.location.href = "/sign-in";
            return {
              error: {
                status: 401,
                data: "Unauthorized - Redirecting to sign-in",
              },
            };
          }
        } else {
          // Không có refreshToken

          console.log("No refresh token available");

          removeToken("accessToken");
          removeToken("refreshToken");
          window.location.href = "/sign-in";
          return {
            error: {
              status: 401,
              data: "Unauthorized - Redirecting to sign-in",
            },
          };
        }
      }

      return {
        error: {
          status: err.response?.status,
          data: err.response?.data || err.message,
        },
      };
    }
  };

export const apiSlice = createApi({
  reducerPath: "api",
  baseQuery: axiosBaseQuery({
    baseUrl: process.env.REACT_APP_BASE_URL,
  }),
  tagTypes: [
    "User",
    "Quests",
    "OngoingQuests",
    "FavoriteLocations",
    "UnreadCount",
    "InboxList",
    "UnreadQuestHistoryCount",
    "Tutorials",
    "WithdrawHistory",
    "Locations",
    "QuestDetails",
    "QuestList",
    "StickerList",
    "Businesses",
    "Businesses_Favorite",
    "Outlet_of_business",
  ],
  endpoints: (builder) => ({
    ...authEndpoints(builder),
    ...dataEndpoints(builder),
  }),
});

export const {
  // auth
  useLoginMutation,
  useLogoutMutation,
  useSignupMutation,
  useVerifyOTPMutation,
  useVerifyOTPSignUpMutation,
  useResendOTPSignUpMutation,
  usePostQrCodeTrackMutation,
  // Profile
  useGetUserInfoQuery,
  useUpdateUserInfoMutation,
  usePostFileUploadMutation,
  usePostUpdateAvatarMutation,
  useLazyGetInboxListQuery,
  useGetUnreadInboxCountQuery,
  usePostReadInboxMutation,
  usePostUpdateNotificationSettingsMutation,
  usePostAcceptTermsOfUseMutation,
  // Quest
  useLazyGetQuestListQuery,
  useLazyGetQuestListPerLocationMainQuery,
  useLazyGetQuestListPerLocationQuery,
  useLazyGetOngoingQuestListPerLocationQuery,
  useLazyGetFavoriteLocationsQuery,
  useLazyGetQuestHistoryListQuery,
  useGetUnreadQuestHistoryCountQuery,
  usePostReadQuestHistoryMutation,
  useGetTakeQuestQuery,
  usePostTakeQuestPracticeMutation,
  usePostAnswerMutation,
  usePostQuestReviewMutation,
  useGetQuestDetailsQuery,
  usePostEndQuestMutation,
  usePostSaveQuestMutation,
  usePostStartQuestMutation,
  useGetLocationByLatLongQuery,
  useLazyGetTimeExtensionQuery,
  useGetQuestDetailsPublicQuery,
  usePostPauseTimerParticipantMutation,
  usePostCreateReferralCodeMutation,
  useLazyGetValidateReferralCodeQuery,
  usePostCompleteGuideOneMutation,
  // useLazyGetQuestDetailsQuery,
  useLazyGetTakeQuestPracticeQuery,
  useLazyGetDetailParticipantQuery,
  usePostFileTakeQuestMutation,
  usePostTakeQuestCompleteMutation,
  useFavoritesQuestMutation,
  //tutorials
  useGetTutorialsQuery,
  useGetTutorialDetailsQuery,
  usePostTutorialStatusMutation,
  usePostNextTutorialMutation,
  //withdraw
  usePostWithdrawMutation,
  useGetWithdrawHistoryQuery,
  useGetWithdrawDetailsQuery,
  useGetCalculateTokenQuery,
  //Contact
  usePostContactMutation,

  // Location
  // useLazyGetQuestListPerLocationMainQuery,
  usePostFavoriteLocationMutation,

  // Sticker
  useGetStickerListQuery,
  useLazyGetRewardsByStickerQuery,
  useLazyGetCheckExistLocationQuery,
  usePostRedeemStickerMutation,
  useLazyGetRedemptionHistoryQuery,

  // Business
  useGetBusinessesQuery,
  useGetBusinessesFavoriteQuery,
  useGetBusinessDetailQuery,
  usePostFavoriteBusinessMutation,
  usePostFavoritesLocationMutation,
  useLazyGetOutletOfBusinessQuery,
} = apiSlice;
